<template lang="pug">
  .expandable
    .expandable__toggle(
      :class="{ 'expandable__toggle_active': isOpen, 'expandable__toggle_disabled': isAnimating }"
      :aria-expanded="isOpen"
      @click="toggle"
    )
      slot(name="toggle")
    .expandable__body(ref="bodyElement" @transitionend="onTransitionEnd")
      .expandable__layout(ref="layoutElement")
        slot(name="expandable")
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    mobileOff?: boolean
    open?: boolean
    recalc?: boolean
  }>(),
  { mobileOff: false, open: false, recalc: false },
)

const bodyElement = ref<HTMLElement | null>(null)
const layoutElement = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const isAnimating = ref(false)
const interactionDisabled = ref(false)
let resizeObserver: ResizeObserver | undefined

const setExpandedHeight = (animate = true): void => {
  if (!bodyElement.value || !layoutElement.value) return

  bodyElement.value.style.transition = animate ? '' : 'none'
  bodyElement.value.style.height = `${layoutElement.value.offsetHeight}px`
  bodyElement.value.style.overflow = animate ? 'hidden' : 'visible'
  isOpen.value = true
  isAnimating.value = animate

  if (!animate) requestAnimationFrame(() => bodyElement.value?.style.removeProperty('transition'))
}

const collapse = (): void => {
  if (!bodyElement.value) return
  bodyElement.value.style.height = '0px'
  bodyElement.value.style.overflow = 'hidden'
  isOpen.value = false
  isAnimating.value = true
}

const toggle = (): void => {
  if (interactionDisabled.value || isAnimating.value) return
  if (isOpen.value) collapse()
  else setExpandedHeight(true)
}

const onTransitionEnd = (event: TransitionEvent): void => {
  if (event.propertyName !== 'height' || event.target !== bodyElement.value) return
  isAnimating.value = false
  if (isOpen.value && bodyElement.value) bodyElement.value.style.overflow = 'visible'
}

watch(
  () => props.open,
  async (open) => {
    await nextTick()
    if (open) setExpandedHeight(false)
    else collapse()
  },
)

watch(
  () => props.recalc,
  async () => {
    await nextTick()
    if (isOpen.value) setExpandedHeight(false)
  },
)

onMounted(() => {
  interactionDisabled.value = props.mobileOff && window.matchMedia('(max-width: 660px)').matches

  if (interactionDisabled.value) setExpandedHeight(false)
  else if (props.open) setExpandedHeight(false)

  if (layoutElement.value) {
    resizeObserver = new ResizeObserver(() => {
      if (isOpen.value) setExpandedHeight(false)
    })
    resizeObserver.observe(layoutElement.value)
  }
})

onBeforeUnmount(() => resizeObserver?.disconnect())
</script>
